import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Configuration options for the useCarousel hook.
 *
 * @interface UseCarouselOptions
 * @member {string} containerId - The DOM element ID of the carousel container
 * @member {number} totalItems - Total number of items in the carousel
 * @member {number} autoScrollInterval - Auto-scroll interval in milliseconds (default: 5000)
 * @member {boolean} isMobile - Whether the carousel is in mobile mode (default: false)
 * @member {string} itemSelector - CSS selector for targeting specific items in mobile view (e.g., '.imageWrapper')
 */
export interface UseCarouselOptions {
  containerId: string;
  totalItems: number;
  autoScrollInterval?: number;
  isMobile?: boolean;
  itemSelector?: string;
}

/**
 * Return interface for the useCarousel hook.
 *
 * @interface UseCarouselReturn
 * @member {number} activeIndex - Current active item index (0-based)
 * @member {function} handlePaginationClick - Function to manually navigate to a specific item by index
 * @member {number} totalItems - Total number of items in the carousel
 */
export interface UseCarouselReturn {
  activeIndex: number;
  handlePaginationClick: (index: number) => void;
  totalItems: number;
}

/**
 * A reusable carousel hook that provides automatic scrolling and manual navigation functionality.
 * Supports both desktop (scroll by container width) and mobile (scroll to specific items) modes.
 * Handles smooth transitions, automatic progression, and manual pagination controls.
 *
 * @param {UseCarouselOptions} options - Configuration options for the carousel
 * @param {string} options.containerId - The DOM element ID of the carousel container
 * @param {number} options.totalItems - Total number of items in the carousel
 * @param {number} options.autoScrollInterval - Auto-scroll interval in milliseconds
 * @param {boolean} options.isMobile - Whether the carousel is in mobile mode
 * @param {string} options.itemSelector - CSS selector for targeting specific items in mobile view
 *
 * @returns {UseCarouselReturn} Object containing carousel state and control functions
 *
 * @example
 * // Basic desktop carousel
 * const { activeIndex, handlePaginationClick } = useCarousel({
 *   containerId: "my-carousel",
 *   totalItems: 5,
 *   autoScrollInterval: 3000
 * });
 *
 * @example
 * // Mobile carousel with specific item targeting
 * const { activeIndex, handlePaginationClick } = useCarousel({
 *   containerId: "mobile-carousel",
 *   totalItems: 3,
 *   autoScrollInterval: 4000,
 *   isMobile: true,
 *   itemSelector: ".carousel-item"
 * });
 */
export const useCarousel = ({
  containerId,
  totalItems,
  autoScrollInterval = 5000,
  isMobile = false,
  itemSelector,
}: UseCarouselOptions): UseCarouselReturn => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isAutoScrolling = useRef(false);

  /**
   * Scrolls the carousel to a specific item index with smooth animation.
   * Handles both mobile (scrollIntoView) and desktop (scrollTo) behaviors.
   *
   * @param {number} index - The target item index (0-based)
   */
  const scrollToIndex = useCallback(
    (index: number) => {
      const container = document.getElementById(containerId);
      if (!container) return;

      isAutoScrolling.current = true;

      if (isMobile && itemSelector) {
        // Mobile: scroll to specific item using scrollIntoView
        const targetItem = container.querySelector(
          `${itemSelector}[data-position="${index + 1}"]`,
        ) as HTMLElement;

        if (targetItem) {
          targetItem.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      } else {
        // Desktop: scroll by container width
        container.scrollTo({
          left: index * container.offsetWidth,
          behavior: "smooth",
        });
      }

      setTimeout(
        () => {
          isAutoScrolling.current = false;
          setActiveIndex(index);
        },
        isMobile ? 300 : 1000,
      );
    },
    [containerId, isMobile, itemSelector],
  );

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const handleScroll = () => {
      if (isAutoScrolling.current) return;

      if (isMobile && itemSelector) {
        // Mobile: calculate which item is in center view
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;

        let closestIndex = 0;
        let closestDistance = Infinity;

        for (let i = 0; i < totalItems; i++) {
          const item = container.querySelector(
            `${itemSelector}[data-position="${i + 1}"]`,
          ) as HTMLElement;

          if (item) {
            const itemRect = item.getBoundingClientRect();
            const itemCenter = itemRect.left + itemRect.width / 2;
            const distance = Math.abs(containerCenter - itemCenter);

            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = i;
            }
          }
        }

        if (closestIndex !== activeIndex) {
          setActiveIndex(closestIndex);
        }
      } else {
        // Desktop: calculate by scroll position
        const newIndex = Math.round(
          container.scrollLeft / container.offsetWidth,
        );
        if (newIndex !== activeIndex) {
          setActiveIndex(newIndex);
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeIndex, isMobile, itemSelector, totalItems, containerId]);

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (activeIndex + 1) % totalItems;
      scrollToIndex(nextIndex);
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [activeIndex, totalItems, autoScrollInterval, scrollToIndex]);

  return {
    activeIndex,
    handlePaginationClick: scrollToIndex,
    totalItems,
  };
};
